import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Menu, Popover } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { normalizeVariableLabel } from '~/components/AgreementStudioPage/TemplateVariables/TemplateVariables.utils';
import { routes } from '~/constants';
import { useLocaleKeys } from '~/i18n';
import styles from './VariablePickerPopover.module.scss';
import { buildSelectableItems, canCreateVariable, getCategoryForItem } from './VariablePickerPopover.utils';
// Picker has no DOM element of its own — its root IS the Menu's root. Use GetMenuTID(testId).MenuTID.Root
// from a caller's driver to assert the picker is open.
export const GetVariablePickerPopoverTID = (parentTestId)=>getTestIds(parentTestId);
const headerIdFor = (item)=>{
    var _item_subcategory;
    return "header-".concat(item.category, "-").concat((_item_subcategory = item.subcategory) !== null && _item_subcategory !== void 0 ? _item_subcategory : '');
};
const itemIdFor = (item)=>{
    var _item_subcategory;
    return "item-".concat(item.category, "-").concat((_item_subcategory = item.subcategory) !== null && _item_subcategory !== void 0 ? _item_subcategory : '', "-").concat(item.label);
};
function buildMenuItems(selectableItems, testIds, onSelect) {
    return selectableItems.flatMap((item, index)=>{
        const header = getCategoryForItem(selectableItems, index);
        const headerEntry = header != null ? [
            {
                id: headerIdFor(item),
                isSectionHeader: true,
                label: header,
                testId: testIds.CategoryHeader(header)
            }
        ] : [];
        const itemEntry = {
            id: itemIdFor(item),
            title: item.label,
            testId: testIds.VariableItem(item.label),
            onClick: ()=>onSelect(item.label),
            bi: {
                name: 'variable_picker_item',
                data: {
                    label: item.label
                }
            }
        };
        return [
            ...headerEntry,
            itemEntry
        ];
    });
}
export function VariablePickerPopover(param) {
    let { anchorEl, onSelect, onClose, excludeLabels = [], allowedSystemKeys, customTags, testId } = param;
    const { VariablePickerPopoverTestIds } = useMemo(()=>GetVariablePickerPopoverTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys('components.templateVariables.picker');
    const history = useHistory();
    const customTagsEnabled = customTags != null;
    const normalizedExcludeLabels = useMemo(()=>new Set(excludeLabels.map(normalizeVariableLabel)), [
        excludeLabels
    ]);
    const selectableItems = useMemo(()=>{
        var _ref;
        return buildSelectableItems((_ref = customTags === null || customTags === void 0 ? void 0 : customTags.variables) !== null && _ref !== void 0 ? _ref : [], normalizedExcludeLabels, {
            allowedSystemKeys
        });
    }, [
        customTags === null || customTags === void 0 ? void 0 : customTags.variables,
        normalizedExcludeLabels,
        allowedSystemKeys
    ]);
    const items = useMemo(()=>buildMenuItems(selectableItems, VariablePickerPopoverTestIds, onSelect), [
        selectableItems,
        VariablePickerPopoverTestIds,
        onSelect
    ]);
    return /*#__PURE__*/ _jsx(Popover, {
        open: !!anchorEl,
        anchorEl: anchorEl,
        onClose: onClose,
        anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right'
        },
        transformOrigin: {
            vertical: -4,
            horizontal: 'right'
        },
        disableEnforceFocus: true,
        children: /*#__PURE__*/ _jsx(Menu, {
            flat: true,
            testId: testId,
            items: items,
            emptyText: customTagsEnabled ? undefined : t.noMatches(),
            dimensions: {
                width: 320,
                maxHeight: 360
            },
            searchProps: {
                bi: {
                    name: 'variable_picker_search'
                },
                showSearch: true,
                autoFocus: true,
                placeholder: t.searchPlaceholder(),
                strategy: {
                    type: 'custom',
                    filterItem: (search, item)=>{
                        if (typeof item.title !== 'string') return false;
                        return normalizeVariableLabel(item.title).includes(normalizeVariableLabel(search.trim()));
                    }
                },
                action: customTagsEnabled ? {
                    bi: {
                        name: 'create_variable'
                    },
                    label: t.create(),
                    shouldShow: (search)=>canCreateVariable(search, customTags.variables),
                    onSubmit: onSelect,
                    suffixIcon: /*#__PURE__*/ _jsx("kbd", {
                        className: styles.kbdHint,
                        children: "⏎"
                    })
                } : undefined
            },
            footer: customTagsEnabled ? {
                items: [
                    {
                        variant: 'promotionalAction',
                        props: {
                            bi: {
                                name: 'manage_custom_tags'
                            },
                            title: t.manageCustomTags(),
                            body: t.manageCustomTagsDescription(),
                            testId: VariablePickerPopoverTestIds.ManageCustomTags(),
                            onClick: ()=>{
                                onClose();
                                history.push("".concat(routes.proposalDefaults, "#custom-tags"));
                            }
                        }
                    }
                ]
            } : undefined
        })
    });
}
